import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { signIn, signUp } from '../lib/auth'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'signin' | 'signup'
}

export function AuthModal({ isOpen, onClose, mode: initialMode }: AuthModalProps) {
  const navigate = useNavigate()
  const [mode, setMode] = useState(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode)
      setEmail('')
      setPassword('')
      setError('')
      setIsLoading(false)
    }
  }, [isOpen, initialMode])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      if (mode === 'signin') {
        await signIn.email({
          email,
          password,
          callbackURL: '/',
        })
      } else {
        await signUp.email({
          email,
          password,
          callbackURL: '/',
        })
      }
      onClose()
      window.location.reload() // Reload to update auth state
    } catch (err: any) {
      setError(err.message || `Failed to ${mode === 'signin' ? 'sign in' : 'sign up'}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSocialAuth = async (provider: 'google' | 'apple') => {
    try {
      await signIn.social({
        provider,
        callbackURL: '/',
      })
      onClose()
    } catch (err: any) {
      setError(err.message || `Failed to authenticate with ${provider}`)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}
        onClick={onClose}
      >
        {/* Modal */}
        <div 
          style={{
            maxWidth: '400px',
            width: '100%',
            padding: '2rem',
            backgroundColor: 'var(--sacred-bg-secondary)',
            borderRadius: '0.5rem',
            border: '1px solid var(--sacred-border)',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'none',
              border: 'none',
              color: 'var(--sacred-text-dim)',
              cursor: 'pointer',
              fontSize: '1.5rem',
              lineHeight: 1
            }}
          >
            ×
          </button>

          <h1 style={{ marginBottom: '2rem', textAlign: 'center' }}>
            {mode === 'signin' ? 'SIGN IN' : 'SIGN UP'}
          </h1>
          
          {error && (
            <div style={{
              padding: '1rem',
              marginBottom: '1rem',
              backgroundColor: 'var(--sacred-error-bg)',
              color: 'var(--sacred-error)',
              borderRadius: '0.25rem',
              border: '1px solid var(--sacred-error)'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                EMAIL
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  backgroundColor: 'var(--sacred-bg)',
                  border: '1px solid var(--sacred-border)',
                  borderRadius: '0.25rem',
                  color: 'var(--sacred-text)'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                PASSWORD
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  backgroundColor: 'var(--sacred-bg)',
                  border: '1px solid var(--sacred-border)',
                  borderRadius: '0.25rem',
                  color: 'var(--sacred-text)'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--sacred-success)',
                color: 'var(--sacred-bg)',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                marginBottom: '1rem'
              }}
            >
              {isLoading 
                ? (mode === 'signin' ? 'SIGNING IN...' : 'SIGNING UP...') 
                : (mode === 'signin' ? 'SIGN IN' : 'SIGN UP')
              }
            </button>
          </form>

          <div style={{ marginBottom: '1rem', textAlign: 'center', color: 'var(--sacred-text-dim)' }}>
            OR
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button
              onClick={() => handleSocialAuth('google')}
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--sacred-bg)',
                color: 'var(--sacred-text)',
                border: '1px solid var(--sacred-border)',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {mode === 'signin' ? 'SIGN IN' : 'SIGN UP'} WITH GOOGLE
            </button>

            <button
              onClick={() => handleSocialAuth('apple')}
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--sacred-bg)',
                color: 'var(--sacred-text)',
                border: '1px solid var(--sacred-border)',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              {mode === 'signin' ? 'SIGN IN' : 'SIGN UP'} WITH APPLE
            </button>
          </div>

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <span style={{ color: 'var(--sacred-text-dim)' }}>
              {mode === 'signin' ? "DON'T HAVE AN ACCOUNT? " : "ALREADY HAVE AN ACCOUNT? "}
            </span>
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setMode(mode === 'signin' ? 'signup' : 'signin')
                setError('')
              }}
              style={{ color: 'var(--sacred-link)', textDecoration: 'underline' }}
            >
              {mode === 'signin' ? 'SIGN UP' : 'SIGN IN'}
            </a>
          </div>
        </div>
      </div>
    </>
  )
}